import React, { useEffect } from "react";

import { Stack, Button, Dialog, Typography, IconButton, Tooltip } from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from '@mui/icons-material/Close';

import { urlExpired } from "../../../../utils/url-expired";

import TelehealthImage from "./telehealth-image";
import ActivateLiveAnnotationButton from "./activate-live-annotation-button";

interface ImageViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  downloadURLs: string[];
  patientId: string;
  currentImageIndex: number;
  onImageIndexChange: (index: number) => void;
  height?: number
}

const ImageViewerModal: React.FC<ImageViewerModalProps> = ({
  isOpen,
  onClose,
  downloadURLs,
  patientId,
  currentImageIndex,
  onImageIndexChange,
  height = 80
}) => {

  const currentImage =
    currentImageIndex > -1 && currentImageIndex < downloadURLs.length
      ? downloadURLs[currentImageIndex]
      : "";

  const title = "Image Preview";

  const currentExpired = urlExpired(currentImage);

  // Calculate 16:9 aspect ratio dimensions - use viewport height as base
  const modalHeight = Math.min(height, 90); // Cap at 90vh to ensure it fits
  const modalWidth = (modalHeight * 16) / 9; // 16:9 aspect ratio

  const heightSize = modalHeight + "vh";
  const widthSize = modalWidth + "vh"; // Use vh for both to maintain ratio

  const acceptDownload = () => {
    if (currentImage) {
      downloadResource(
        currentImage,
        `${patientId} image ${currentImageIndex + 1}.jpg`
      );
    }
  };

  const onLiveAnnotationChange = (annotation: boolean) => {
    if (annotation) {
      onClose();
    }
  };

  const onNavigateNext = () => {
    if (downloadURLs.length < 2) {
      return;
    }

    if (currentImageIndex + 1 < downloadURLs.length) {
      onImageIndexChange(currentImageIndex + 1);
    } else {
      onImageIndexChange(0);
    }
  };

  const onNavigatePrevious = () => {
    if (downloadURLs.length < 2) {
      return;
    }

    if (currentImageIndex - 1 >= 0) {
      onImageIndexChange(currentImageIndex - 1);
    } else {
      onImageIndexChange(downloadURLs.length - 1);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          onNavigatePrevious();
          break;
        case 'ArrowRight':
          event.preventDefault();
          onNavigateNext();
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onNavigateNext, onNavigatePrevious, onClose]);

  const forceDownload = (blobUrl: string, filename: string) => {
    const a = document.createElement("a");
    a.download = filename;
    a.href = blobUrl;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const downloadResource = (url: string, filename?: string) => {
    if (!filename)
      filename = url.split("\\").pop()?.split("/").pop() || "download";
    fetch(url, {
      headers: new Headers({
        Origin: location.origin,
      }),
      mode: "cors",
    })
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        forceDownload(blobUrl, filename!);
        // Optionally revoke the object URL after a delay
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      })
      .catch((e) => console.error(e));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      fullWidth={false}
      sx={{
        '& .MuiPaper-root': {
          background: "#000000ff",
          width: widthSize,
          height: heightSize,
          minWidth: 'unset',
          maxWidth: 'unset',
          overflow: 'hidden',
          margin: 'auto',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.05)',
          padding: 0,
          '& > *': {
            margin: 0,
            padding: 0,
          }
        },
        '& .MuiDialog-container': {
          overflow: 'hidden',
        },
      }}
    >
      <Stack
        alignItems={"center"}
        alignContent={"center"}
        justifyItems={"center"}
        justifyContent={"center"}
        height={"100%"}
        width={"100%"}
        sx={{
          backgroundColor: "#333333",
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Navigation buttons positioned relative to the full Stack */}
        <Button
          variant="text"
          disabled={downloadURLs.length < 2}
          onClick={onNavigatePrevious}
          sx={{
            position: "absolute",
            top: "0px",
            left: "0px",
            height: "100%",
            width: "100px",
            minWidth: "100px",
            zIndex: 9,
            background: "linear-gradient(to right, rgba(0, 0, 0, 0.2), transparent)",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            '&:hover': {
              background: "linear-gradient(to right, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.1))",
              color: "white",
              '& .MuiSvgIcon-root': {
                opacity: 1,
              },
            },
            '&:disabled': {
              background: "linear-gradient(to right, rgba(0, 0, 0, 0.1), transparent)",
              color: "rgba(255, 255, 255, 0.3)",
            },
            '& .MuiSvgIcon-root': {
              opacity: 0.3, // Make slightly visible by default
              fontSize: "3rem",
              transition: "opacity 0.2s ease-in-out",
            },
          }}
        >
          <ArrowBackIcon />
        </Button>

        <Button
          variant="text"
          disabled={downloadURLs.length < 2}
          onClick={onNavigateNext}
          sx={{
            position: "absolute",
            top: "0px",
            right: "0px",
            height: "100%",
            width: "100px",
            minWidth: "100px",
            zIndex: 9,
            background: "linear-gradient(to left, rgba(0, 0, 0, 0.2), transparent)",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            '&:hover': {
              background: "linear-gradient(to left, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.1))",
              color: "white",
              '& .MuiSvgIcon-root': {
                opacity: 1,
              },
            },
            '&:disabled': {
              background: "linear-gradient(to left, rgba(0, 0, 0, 0.1), transparent)",
              color: "rgba(255, 255, 255, 0.3)",
            },
            '& .MuiSvgIcon-root': {
              opacity: 0.3, // Make slightly visible by default
              fontSize: "3rem",
              transition: "opacity 0.2s ease-in-out",
            },
          }}
        >
          <ArrowForwardIcon />
        </Button>

        <div style={{
          position: "relative",
          width: "100%",
          height: "100%",
          alignContent: "center",
          alignItems: "center",
          justifyContent: "center",
          justifyItems: "center"
        }}>
          <Stack
            direction="row"
            spacing={2}
            justifyContent={"space-between"}
            alignItems={"center"}
            width={"100%"}
            sx={{
              position: "absolute",
              top: "0px",
              zIndex: 10,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              paddingY: 1,
            }}
          >
            <Stack sx={{
              paddingLeft: 2
            }}>
              <Typography variant="h5"
                sx={{
                  color: "white",
                  //marginBottom: "4px",
                  flexShrink: 0, // Prevent text from shrinking on small screens
                }}>
                {title}
              </Typography>
            </Stack>
            <Stack>
              <ActivateLiveAnnotationButton
                expired={currentExpired}
                urlIndex={currentImageIndex}
                liveAnnotationChanged={onLiveAnnotationChange}
                closeToShowAnnotation={onClose}
              />
            </Stack>
            <Stack direction={"row"} sx={{
              paddingRight: 1
            }}>
              <IconButton
                disabled={downloadURLs.length < 1 || currentExpired}
                onClick={acceptDownload}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                  },
                }}>
                <DownloadIcon />
              </IconButton>
              <IconButton
                onClick={onClose}
                aria-label="close"
                sx={{
                  color: (theme) => theme.palette.grey[500],
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </div>

        {/* Image dots indicator */}
        {downloadURLs.length > 1 && (
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="center"
            alignItems="center"
            sx={{
              position: "absolute",
              bottom: "16px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              borderRadius: "20px",
              paddingX: 2,
              paddingY: 1,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {downloadURLs.map((_, index) => (
                <Button
                  key={index}
                  onClick={() => onImageIndexChange(index)}
                  sx={{
                    minWidth: "unset",
                    width: index === currentImageIndex ? "12px" : "8px",
                    height: index === currentImageIndex ? "12px" : "8px",
                    borderRadius: "50%",
                    padding: 0,
                    backgroundColor: index === currentImageIndex
                      ? "#2196f3"
                      : "rgba(255, 255, 255, 0.5)",
                    '&:hover': {
                      backgroundColor: index === currentImageIndex
                        ? "#1976d2"
                        : "rgba(255, 255, 255, 0.8)",
                    },
                    '&:active': {
                      transform: "scale(0.9)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                />
              ))}
            </Stack>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                fontSize: "0.75rem",
                fontWeight: 500,
                userSelect: "none",
                display: "flex",
                alignItems: "center",
                lineHeight: 1,
              }}
            >
              {currentImageIndex + 1}/{downloadURLs.length}
            </Typography>
          </Stack>
        )}

        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 0,
          fontSize: 0
        }}>
          <TelehealthImage currentImage={currentImage} height={height} />
        </div>
      </Stack>
    </Dialog >
  );
};

export default ImageViewerModal;
